import CloseIcon from "@mui/icons-material/Close";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { Stack } from "@mui/material";
import { useState } from "react";
import {
  defaultTransition,
  miniGap,
  perelynBlack,
  perelynPrimary,
} from "../../../styles/consts";
import { StyledRowDataBox } from "../../styles/Table.styles";
import { SmallText } from "../../styles/Text.styles";
import Checkbox from "../fundamentals/Checkbox";
import Icon from "../fundamentals/Icon";
import ColumnFilter, { FilterValue } from "./ColumnFilter";
import { ItemRow } from "./Row";
import { Sort } from "./Table";

const labelRowIconSize = "1.1rem";

export default function LabelRow({
  labels,
  handleAllDisplayedSelect,
  isChecked,
  changeSort,
  currentSort,
  onFilterChange,
  activeFilters,
}: {
  labels: ItemRow[];
  handleAllDisplayedSelect: () => void;
  isChecked: boolean;
  changeSort?: (param?: string) => void;
  currentSort?: Sort;
  onFilterChange?: (param: string, value: FilterValue | null) => void;
  activeFilters?: Record<string, FilterValue>;
}) {
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const [currentFilterParam, setCurrentFilterParam] = useState<string | null>(
    null
  );

  const handleFilterClick = (event: any, param: string) => {
    setFilterAnchorEl(event.currentTarget);
    setCurrentFilterParam(param);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
    setCurrentFilterParam(null);
  };

  const handleFilterApply = (value: FilterValue) => {
    if (currentFilterParam && onFilterChange) {
      onFilterChange(currentFilterParam, value);
    }
  };

  return (
    <Stack
      sx={{
        flexDirection: "row",
        width: "100%",
      }}
    >
      {labels.map((label: ItemRow, idx) => (
        <StyledRowDataBox
          key={idx}
          isLast={idx === labels.length - 1}
          width={label.width}
        >
          {idx === 0 && (
            <Checkbox
              id={"select-all-checkbox"}
              iconName="Minus"
              onChange={handleAllDisplayedSelect}
              isChecked={isChecked}
            />
          )}
          {label.label && (
            <Stack
              sx={{
                flexDirection: "row",
                alignItems: "center",
                gap: miniGap,
                cursor: label.sortParam ? "crosshair" : "default",
                width: "100%",
              }}
            >
              <Stack
                sx={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: miniGap,
                }}
                onClick={() => changeSort && changeSort(label.sortParam)}
              >
                <SmallText
                  sx={{
                    whiteSpace: "pre-line",
                    textAlign: "center",
                    justifyContent: "center",
                  }}
                >
                  {label.label}
                </SmallText>
                {currentSort?.param === label.sortParam && (
                  <Icon
                    name="ArrowDown"
                    sx={{
                      width: labelRowIconSize,
                      height: labelRowIconSize,
                      transition: defaultTransition,
                      transform:
                        currentSort?.order === "asc" ? "scaleY(-1)" : "none",
                    }}
                  />
                )}
              </Stack>
              {label.filterConfig && (
                <Stack
                  sx={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: miniGap,
                  }}
                >
                  <FilterAltIcon
                    onClick={(e) => handleFilterClick(e, label.sortParam || "")}
                    sx={{
                      width: labelRowIconSize,
                      height: labelRowIconSize,
                      cursor: "pointer",
                      color:
                        activeFilters?.[label.sortParam || ""] &&
                        activeFilters?.[label.sortParam || ""]?.value
                          ? perelynPrimary
                          : perelynBlack,
                    }}
                  />
                  {activeFilters?.[label.sortParam || ""] &&
                    activeFilters?.[label.sortParam || ""]?.value && (
                      <CloseIcon
                        onClick={(e) => {
                          e.stopPropagation();
                          onFilterChange &&
                            onFilterChange(label.sortParam || "", null);
                        }}
                        sx={{
                          width: labelRowIconSize,
                          height: labelRowIconSize,
                          cursor: "pointer",
                          color: perelynBlack,
                          transition: defaultTransition,
                        }}
                      />
                    )}
                </Stack>
              )}
            </Stack>
          )}
        </StyledRowDataBox>
      ))}
      {currentFilterParam && (
        <ColumnFilter
          anchorEl={filterAnchorEl}
          onClose={handleFilterClose}
          onApply={handleFilterApply}
          config={
            labels.find((l) => l.sortParam === currentFilterParam)
              ?.filterConfig!
          }
          initialValue={activeFilters?.[currentFilterParam]}
        />
      )}
    </Stack>
  );
}
